import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { MonthPickerField } from "../../../../../common/selectComponents/MonthPicker/MonthPickerField";
import { YearPickerField } from "../../../../../common/selectComponents/YearPicker/YearPickerField";

export function ProcessLeaveIncentiveForm({
  isOpen,
  onClose,
  progress,
  simulateLoading,
  stopSimulation,
  setLongLoadingRequest,
  setLoadingPercentage,
  refresh,
  showGeneralMessage,
  processLeaveIncentive,
  triggerConfirm,
}) {
  // ADDED: Pattern-aligned empty form
  const emptyForm = {
    month: "",
    year: null,
  };

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  // ADDED: set helper function
  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // MODIFIED: Reset form and errors on close
  const handleClose = () => {
    onClose();
    setForm(emptyForm);
    setErrors({});
  };

  // ADDED: Validation logic matching AddEditCrewDefinition pattern
  const validate = () => {
    const e = {};
    if (!form.month) e.month = "Month is required.";
    if (!form.year) e.year = "Year is required.";
    return e;
  };

  // MODIFIED: Blocks processing if validation fails
  const handleSubmit = (e) => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    
    triggerConfirm(
      "Confirm Process",
      `Are you sure you want to process the leave incentive for ${form.month} ${form.year}?`,
      () => handleProcess(e)
    );
  };

  const handleProcess = async (event) => {
    event.preventDefault();

    try {
      setLoadingPercentage(10);
      setLongLoadingRequest(true);

      simulateLoading(30, 200);

      const payload = {
        month: form.month,
        year: Number(form.year),
      }
      // MODIFIED: Pass values from the form object
      await processLeaveIncentive(payload);

      setLoadingPercentage(30);
      await simulateLoading(60, 100);
      await simulateLoading(70, 100);
      await simulateLoading(80, 100);
      await simulateLoading(90, 100);

      await refresh();
      simulateLoading(95, 100);

      showGeneralMessage("Leave incentive has been processed!", true);
      await simulateLoading(100, 10);

      handleClose();
    } catch (error) {
      stopSimulation();

      console.error("Error:", error);

      let errorMessage = "Error processing request!";
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data || "Server Error: " + error.response.status;
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
      } else {
        errorMessage = error.message;
      }

      showGeneralMessage(errorMessage, false);

    } finally {
      setTimeout(() => {
        setLoadingPercentage(0);
        setLongLoadingRequest(false);
      }, 300);
    }

  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3"
        style={{ marginTop: "0px" }}
      >
        <div className="flex w-full flex-col rounded-lg bg-white shadow-xl md:w-[500px]">
          <div className="flex items-center justify-between px-6 py-4 shadow-sm">
            <h2 className="text-xl font-bold text-customMainColor">
              Process Leave Incentive
            </h2>
            <button
              onClick={handleClose}
              className="cursor-pointer border-none bg-transparent"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="bg-gray-50 p-6">
            <div className="space-y-4 rounded-lg border border-gray-300 bg-white p-4 shadow-sm">
              {/* MODIFIED: Implemented validation props and form state */}
              <MonthPickerField
                label="Month"
                value={form.month}
                onSelect={(monthName) => set("month", monthName)}
                onClear={() => set("month", "")}
                placeholder={"--Select Month--"}
                isRequired={true}
                error={!!errors.month}
                errorMessage={errors.month}
              />

              {/* MODIFIED: Implemented validation props and form state */}
              <YearPickerField
                label="Year"
                value={form.year}
                onSelect={(yearValue) => set("year", yearValue)}
                onClear={() => set("year", "")}
                placeholder={"--Select Year--"}
                isRequired={true}
                error={!!errors.year}
                errorMessage={errors.year}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 border-t p-5">
            <button
              onClick={handleClose}
              className="cursor-pointer rounded border-none bg-gray-200 px-4 py-2"
            >
              Cancel
            </button>
            <button
              onClick={(e) => handleSubmit(e)}
              className="cursor-pointer rounded border-none bg-blue-600 px-6 py-2 text-white font-bold"
            >
              Process
            </button>
          </div>
        </div>
      </div>
    </>
  );
}